import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { USER_CONVERSATION_ID } from '../../api/conversations.api';
import { queryKeys } from '../../api/queryKeys';
import {
  ConversationGroupListItemResponse,
  ConversationMessageEntity,
  ConversationResponse,
} from '../../types/conversation.types';
import { messagePreview } from './helpers/chatMessage';
import { systemMessageText } from './helpers/chatSystemMessage';

/**
 * The merchant app's Groups tab, as the socket and the open thread keep it.
 *
 * Merchant side only. The admin inbox has its own rules (`useChatInbox`) and
 * never reaches here.
 *
 * The list is keyset paged like the admin inbox, but unlike it a row can be
 * moved by hand: the merchant socket delivers every group message whole, with
 * its sender, so the row's new preview, place and unread count are all known
 * - where admin only ever gets a one-line `inbox_updated` for a thread that is
 * not open. Moving a row up never breaks a cursor already issued: the next
 * page is asked for after the LAST loaded row, and a row that jumped to the top
 * now sorts before every one of them on the server too.
 */

type GroupPages = InfiniteData<ConversationGroupListItemResponse[], unknown>;
type MessagePages = InfiniteData<ConversationMessageEntity[], unknown>;

/** Every cached search of the list, with its data. */
const groupLists = (queryClient: QueryClient) =>
  queryClient.getQueriesData<GroupPages>({
    queryKey: queryKeys.conversations.groupsAll,
  });

/** A row from whichever cached search holds one. */
export const userGroupRow = (
  queryClient: QueryClient,
  conversationId: number | string
) => {
  for (const [, data] of groupLists(queryClient)) {
    const row = data?.pages
      .flat()
      .find((item) => item.id === Number(conversationId));
    if (row) return row;
  }
  return undefined;
};

const groupDetail = (
  queryClient: QueryClient,
  conversationId: number | string
) =>
  queryClient.getQueryData<ConversationResponse>(
    queryKeys.conversations.detail(conversationId)
  );

/**
 * The support thread's real id, once this tab has seen it.
 *
 * The merchant reaches that thread through routes with no id and caches it
 * under a sentinel, so the id is only known from a message in it - optimistic
 * rows excluded, which carry 0 until the server answers.
 */
const supportThreadId = (queryClient: QueryClient) => {
  const data = queryClient.getQueryData<MessagePages>(
    queryKeys.conversations.messages(USER_CONVERSATION_ID)
  );
  const known = data?.pages
    .flat()
    .find((message) => message.conversationId > 0);
  return known ? known.conversationId : null;
};

/**
 * Whether a merchant-side event belongs to the support thread or a group.
 *
 * The socket says nothing about it: both arrive on the same events, with no
 * room and no type, and only the conversation id tells them apart. So the id
 * is looked for where groups are already known - the list, a fetched detail -
 * and compared with the support thread's own. Before either is loaded, the
 * message itself is the evidence, and it is good evidence: the support thread
 * anonymises every vGrubs sender to a null id, has no system rows, and has
 * exactly one outside user, the reader. A group breaks each of those.
 *
 * `unknown` is real - the reader's own message from another tab, in a thread
 * neither cache knows yet - and the caller has to refetch rather than guess,
 * because writing a group's message into the support thread is the one mistake
 * that would show.
 */
export const userThreadKind = (
  queryClient: QueryClient,
  conversationId: number,
  message?: ConversationMessageEntity,
  viewerId?: number | null
): 'support' | 'group' | 'unknown' => {
  if (userGroupRow(queryClient, conversationId)) return 'group';
  if (groupDetail(queryClient, conversationId)) return 'group';

  const supportId = supportThreadId(queryClient);
  if (supportId !== null)
    return supportId === conversationId ? 'support' : 'group';

  if (!message) return 'unknown';
  if (message.side === 'system' || message.systemEvent) return 'group';
  if (!message.sender || message.sender.id === null) return 'support';
  if (message.side === 'internal') return 'group';
  if (viewerId != null && message.sender.id !== viewerId) return 'group';
  return 'unknown';
};

const mapRows = (
  queryClient: QueryClient,
  fn: (
    rows: ConversationGroupListItemResponse[]
  ) => ConversationGroupListItemResponse[]
) => {
  for (const [key] of groupLists(queryClient)) {
    queryClient.setQueryData<GroupPages>(key, (data: GroupPages | undefined) =>
      data ? { ...data, pages: data.pages.map(fn) } : data
    );
  }
};

/**
 * A message landed in a group: move its row to the top with the new preview,
 * and count it unless the reader wrote it or is reading it.
 *
 * Returns false when no cached list holds the row - a group the reader was
 * just added to, or one past the loaded pages - which the caller answers with
 * a refetch, since there is no row to move.
 */
export const bumpUserGroupRow = (
  queryClient: QueryClient,
  message: ConversationMessageEntity,
  { isOpen, viewerId }: { isOpen: boolean; viewerId: number | null }
) => {
  const existing = userGroupRow(queryClient, message.conversationId);
  if (!existing) return false;

  /* Your own message, or a system line about something you did. */
  const isMine =
    viewerId !== null &&
    (message.sender?.id === viewerId ||
      message.systemMeta?.byUserId === viewerId);
  const counts = !isMine && !isOpen;

  const updated: ConversationGroupListItemResponse = {
    ...existing,
    lastMessageAt: message.createdAt,
    lastMessagePreview:
      message.side === 'system'
        ? systemMessageText(message, viewerId)
        : messagePreview(message),
    lastMessageSide: message.side,
    unread: counts ? existing.unread + 1 : existing.unread,
    hasMention:
      existing.hasMention ||
      (counts &&
        viewerId !== null &&
        message.mentionUserIds.includes(viewerId)),
  };

  for (const [key] of groupLists(queryClient)) {
    queryClient.setQueryData<GroupPages>(
      key,
      (data: GroupPages | undefined) => {
        if (
          !data?.pages.some((page) => page.some((row) => row.id === updated.id))
        )
          return data;
        const [first = [], ...rest] = data.pages.map((page) =>
          page.filter((row) => row.id !== updated.id)
        );
        return { ...data, pages: [[updated, ...first], ...rest] };
      }
    );
  }
  return true;
};

/** The group was read: its count and its "@" go, wherever it is listed. */
export const clearUserGroupUnread = (
  queryClient: QueryClient,
  conversationId: number | string
) =>
  mapRows(queryClient, (rows) =>
    rows.map((row) =>
      row.id === Number(conversationId) && (row.unread > 0 || row.hasMention)
        ? { ...row, unread: 0, hasMention: false }
        : row
    )
  );

/** The reader left, or was removed: the row goes at once, before any refetch. */
export const removeUserGroupRow = (
  queryClient: QueryClient,
  conversationId: number | string
) =>
  mapRows(queryClient, (rows) =>
    rows.filter((row) => row.id !== Number(conversationId))
  );

/** Muted or unmuted from the group's details - the row's bell follows. */
export const patchUserGroupRow = (
  queryClient: QueryClient,
  conversationId: number | string,
  patch: Partial<ConversationGroupListItemResponse>
) =>
  mapRows(queryClient, (rows) =>
    rows.map((row) =>
      row.id === Number(conversationId) ? { ...row, ...patch } : row
    )
  );

/** What a notification calls the group, and whether it may ring. */
export const describeUserGroup = (
  queryClient: QueryClient,
  conversationId: number | string
) => {
  const row = userGroupRow(queryClient, conversationId);
  const detail = groupDetail(queryClient, conversationId);
  const mutedUntil = row?.mutedUntil ?? detail?.mutedUntil ?? null;
  return {
    name: row?.name ?? detail?.title ?? null,
    muted: !!mutedUntil && new Date(mutedUntil).getTime() > Date.now(),
  };
};
